import { Currency } from 'module/api/account/types';
import { Input } from 'module/ui/components/atoms/input/input';
import { NoValue } from 'module/ui/components/atoms/no-value/empty-cell';
import { useAccountCurrency } from 'module/ui/components/molecules/ag-grid-renderer/cell-renderer/use-account-currency';
import { ICellRendererParams } from 'ag-grid-community';
import { FC, useMemo } from 'react';
import CurrencyFlag, { ICurrencyFlagProps } from 'react-currency-flags';
import styled from 'styled-components';

enum CurrencyPrefixNotation {
  CurrencySymbol = 'CURRENCY_SYMBOL',
  CurrencyCode = 'CURRENCY_CODE',
  CurrencyCodeAndSymbol = 'CURRENCY_CODE_SYMBOL',
}

const K = 10000;
const M = 1000000;
const B = 1000000000;

export const defaultCurrency: Currency = {
  currencyCode: 'USD',
  currencySymbol: '$',
  currencyName: 'US Dollar',
};

const removeTrailingZeroes = (amount: string) => amount.replace(/\.0+$/, '');

export const numberFormatter = (amount: number, precision = 2) =>
  Number(removeTrailingZeroes(amount.toFixed(precision)));

export const getCurrencyPrefix = (
  { currencyCode, currencySymbol } = defaultCurrency,
  notation = CurrencyPrefixNotation.CurrencyCode,
): string => {
  let prefixString = '';

  switch (notation) {
    case CurrencyPrefixNotation.CurrencySymbol:
      prefixString = currencySymbol;
      break;
    case CurrencyPrefixNotation.CurrencyCode:
      prefixString = `${currencyCode} `;
      break;
    case CurrencyPrefixNotation.CurrencyCodeAndSymbol:
      prefixString = `${currencyCode} ${currencySymbol}`;
      break;
    default:
      break;
  }

  return prefixString;
};

const addCurrencyPrefix = (
  val: string | undefined,
  currency: Currency,
  showCurrencyPrefix = true,
  notation: CurrencyPrefixNotation,
): string => {
  if (val == null || Number.isNaN(val)) {
    return '';
  }

  if (!showCurrencyPrefix) {
    return val;
  }

  const prefixString = getCurrencyPrefix(currency, notation);

  return `${prefixString}${val}`;
};

export const currencyRenderer = (
  amount = 0,
  currency = defaultCurrency,
  compactNotation = true,
  showCurrencyPrefix = true,
  precision = 2,
  notation = CurrencyPrefixNotation.CurrencyCode,
): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: precision,
  });

  const formatterWithoutRounding = (value = 0) => {
    const [wholePart, fractionalPart] = value.toString().split('.');

    return [
      formatter.format(Number(wholePart)),
      ...(fractionalPart != null ? [fractionalPart.slice(0, precision)] : []),
    ].join('.');
  };

  const absoluteAmount = Math.abs(amount);

  if (compactNotation) {
    if (absoluteAmount >= K && absoluteAmount < M) {
      const formattedAmount = formatterWithoutRounding(amount / 1000);

      return `${addCurrencyPrefix(
        formattedAmount,
        currency,
        showCurrencyPrefix,
        notation,
      )}K`;
    }
    if (absoluteAmount >= M && absoluteAmount < B) {
      const formattedAmount = formatterWithoutRounding(amount / M);

      return `${addCurrencyPrefix(
        formattedAmount,
        currency,
        showCurrencyPrefix,
        notation,
      )}M`;
    }
    if (absoluteAmount >= B) {
      const formattedAmount = formatterWithoutRounding(amount / B);

      return `${addCurrencyPrefix(
        formattedAmount,
        currency,
        showCurrencyPrefix,
        notation,
      )}B`;
    }
  }

  return `${addCurrencyPrefix(
    formatterWithoutRounding(amount),
    currency,
    showCurrencyPrefix,
    notation,
  )}`;
};

export const currencyFormatterForInput = (
  amount: string | number | undefined,
  currency: Currency,
  compactNotation = true,
  showCurrencyPrefix = true,
  precision = 2,
  notation = CurrencyPrefixNotation.CurrencyCode,
): string => {
  if (amount == null) {
    return '';
  }

  if (typeof amount === 'string') {
    const [wholePart, fractionalPart] = amount.split('.');

    return [
      `${currencyRenderer(
        Number(wholePart ?? 0),
        currency,
        compactNotation,
        showCurrencyPrefix,
        precision,
        notation,
      )}`,
      ...(fractionalPart != null ? [fractionalPart] : []),
    ].join('.');
  }

  return currencyRenderer(
    amount,
    currency,
    compactNotation,
    showCurrencyPrefix,
    precision,
    notation,
  );
};

export const CurrencyFlagWrapper = styled.div`
  display: flex;
  align-items: center;

  > span {
    border-radius: 1.5px;
    box-shadow: 0px 0.2px 0.5px 0px rgba(0, 0, 0, 0.15) inset;
  }
`;

export const CurrencyFlagRenderer: FC<
  React.PropsWithChildren<ICurrencyFlagProps>
> = (props) => (
  <CurrencyFlagWrapper>
    <CurrencyFlag {...props} />
  </CurrencyFlagWrapper>
);

export const StyledCurrencyInput = styled(Input)`
  input::placeholder {
    color: var(--black-100);
  }
`;

export const CurrencyValueCellRenderer: FC<
  React.PropsWithChildren<ICellRendererParams>
> = ({ data, value }) => {
  const currencyMap = useAccountCurrency();

  const currency = useMemo(
    () =>
      data?.projectFinancialsDetailModel?.currency ??
      currencyMap?.default ??
      defaultCurrency,
    [currencyMap?.default, data?.projectFinancialsDetailModel?.currency],
  );

  return (
    <>
      {value == null ? (
        <div>
          <NoValue />
        </div>
      ) : (
        currencyRenderer(value, currency)
      )}
    </>
  );
};
